import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ColumnInput, ColumnLabel, ColumnsContainer } from '../../CreateParam/StructureColumns/style'
import { ParamType } from '../../CreateParam'
import { CertificateType, verifyConformity } from '..'
import { Calculator, ChevronDown } from 'lucide-react'
import { SelectDiv, Dropdown, DropdownItem, DropdownContainer } from '../../../../../components/Modals/EditStructure/style'
import { ColumnType } from '../../../../../components/Modals/EditStructure'

interface PropsType {
  param: ParamType
  structureIndex: number
  certificate: CertificateType
  setCertificate: React.Dispatch<React.SetStateAction<CertificateType>>
  showRange: null | number
}

const StructureColumnsInCertificate: React.FC<PropsType> = ({ param, structureIndex, certificate, setCertificate, showRange }) => {
  const [dropdown, setDropdown] = useState<number | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number, left: number, width: number } | null>(null)
  const dropdownRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null)

  const updateDropdownPosition = (columnId: number) => {
    const dropdownTrigger = dropdownRefs.current.get(columnId)
    if (!dropdownTrigger) return

    const rect = dropdownTrigger.getBoundingClientRect()
    setDropdownPosition({
      top: rect.bottom + 2,
      left: rect.left,
      width: rect.width
    })
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdown !== null) {
        const currentDropdownRef = dropdownRefs.current.get(dropdown)
        const clickedInTrigger = currentDropdownRef?.contains(event.target as Node)
        const clickedInDropdown = dropdownMenuRef.current?.contains(event.target as Node)

        if (!clickedInTrigger && !clickedInDropdown) {
          setDropdown(null)
          setDropdownPosition(null)
        }
      }
    }

    const handleDropdownPosition = () => {
      if (dropdown !== null) {
        updateDropdownPosition(dropdown)
      }
    }

    if (dropdown !== null) {
      updateDropdownPosition(dropdown)
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('resize', handleDropdownPosition)
      document.addEventListener('scroll', handleDropdownPosition, true)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('resize', handleDropdownPosition)
      document.removeEventListener('scroll', handleDropdownPosition, true)
    }
  }, [dropdown])

  const handleChangevalue = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    setCertificate(prev => {
      if (!prev?.columnsInStructureWithValues) {
        return prev;
      }

      return {
        ...prev,
        columnsInStructureWithValues: prev.columnsInStructureWithValues.map(column => (
          column?.id === id ? { ...column, value: e.target.value }
            : column
        ))
      };
    });
  }

  const handleChangeUnit = (id: number, column: ColumnType) => {
    setCertificate(prev => ({
      ...prev,
      columnsInStructureWithValues: prev.columnsInStructureWithValues.map(cV => (
        cV.id === column.id ? { ...cV, unitUsed: { id: id, name: column.possibleUnits?.find((unit: { id: number, name: string }) => unit.id === id)?.name || '' } }
          : cV
      ))
    }))
    setDropdown(null)
  }

  return (
    <ColumnsContainer quantityGrid={param.structures[structureIndex].columns.length}>
      {param.structures[structureIndex].columns.map((column, index) =>
        <ColumnLabel
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          isItLast={index === param.structures[structureIndex].columns.length - 1}
          key={index}
        >
          {column.name} {column.unitUsed?.name ? `` : ''}
          {/* <div title='Calcular Balance'>
            <Calculator size={16} />
          </div> */}
          <DropdownContainer ref={(el) => {
            if (el) {
              dropdownRefs.current.set(column.id, el)
            } else {
              dropdownRefs.current.delete(column.id)
            }
          }}>
            <SelectDiv onClick={() => setDropdown(prev => prev === column.id ? null : column.id)}>
              {certificate.columnsInStructureWithValues.find(item => item?.id === column.id)?.unitUsed?.name ? certificate.columnsInStructureWithValues.find(item => item?.id === column.id)?.unitUsed?.name : '-'}
              <ChevronDown size={12} style={{ position: 'absolute', right: '0' }} />
            </SelectDiv>
            {dropdown === column.id && dropdownPosition && createPortal(
              <Dropdown
                ref={dropdownMenuRef}
                style={{
                  position: 'fixed',
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,
                  width: dropdownPosition.width,
                  zIndex: 9999
                }}
              >
                {column.possibleUnits?.map((item: { id: number, name: string }, index: number) => (
                  <DropdownItem onClick={() => handleChangeUnit(item.id, column)} key={index}>{item.name}</DropdownItem>
                ))}
              </Dropdown>,
              document.body
            )}
          </DropdownContainer>
        </ColumnLabel>
      )}
      {param.structures[structureIndex].columns.map((column, index) =>
        <ColumnInput
          outOfNorm={!verifyConformity(column, certificate)}
          key={index}
          placeholder={showRange === structureIndex ?
            `${column.min ? `${column.min} ≤` : ''} ${column.min || column.max ? 'Valor' : 'Sem restriçoes'} ${column.max ? `≤ ${column.max}` : ''}` :
            `${certificate.columnsInStructureWithValues.find(item => item?.id === column?.id)?.unitUsed?.name ? certificate.columnsInStructureWithValues.find(item => item?.id === column?.id)?.unitUsed?.name : ''}`
          }
          position={index === 0 ? 'first' : index === param.structures[structureIndex].columns.length - 1 ? 'last' : undefined}
          value={showRange !== structureIndex ? certificate.columnsInStructureWithValues.find(item => item?.id === column?.id)?.value || '' : ''}
          onChange={(e) => handleChangevalue(e, column.id)}
        />
      )}
    </ColumnsContainer>
  )
}

export default StructureColumnsInCertificate